import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Ban, Copy, Send } from "lucide-react";
import {
  ApiError,
  apiFetch,
  approveAccessRequest,
  createTeamInvite,
  getAdminAccessRequests,
  getAdminTeamConsents,
  getAdminTeams,
  resolveConsentWithdrawal,
  getTeamInvites,
  rejectAccessRequest,
  resendTeamInvite,
  revokeTeamInvite,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import type {
  AccessRequestRow,
  ConsentWithdrawalRow,
  Player,
  TeamConsentPlayerRow,
  TeamInviteRow,
} from "@/lib/types";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { Button } from "@/components/ui/Button";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { AdminUsersPage } from "./UsersPage";
import { seasonNameOf, teamSeasonLabel } from "@/lib/teamSeasonLabel";

/**
 * M8b E1–E8 — `/admin/people` (membership-scoped people console).
 *
 * Capability gate: page hidden unless the active scope has the
 * `membership:manage` capability OR the user is a global admin
 * (the M8a foundation per-section gate prevents direct nav too).
 *
 * Layout: access requests and no-account invites lead the page. Global
 * admins also see the account directory; each account row owns team
 * status and assignment. Pending/no-account invites are
 * row-owned too: the panel loads every team the signed-in admin can
 * manage, each invite row carries `team_id`, and resend/revoke use
 * that row's team rather than a page-level scope switcher.
 */

const INVITE_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

/** `seasonName` is the team's own season (#908, D1); `null` for a legacy
 * (season-less) team, shown as "Legacy" in the team picker and on the
 * roster option chips. An archived team (a past season) can't be invited
 * to, but keeps naming its existing invites and consents. */
type InviteTeamOption = {
  id: string;
  name: string;
  seasonName?: string | null;
  archived?: boolean;
};

function normalizeInviteTeams<T extends InviteTeamOption>(
  teams: T[],
): InviteTeamOption[] {
  const byId = new Map<string, InviteTeamOption>();
  for (const team of teams)
    byId.set(team.id, {
      id: team.id,
      name: team.name,
      seasonName: team.seasonName ?? null,
      archived: !!team.archived,
    });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function AdminPeoplePage() {
  const [inviteOpenRequest, setInviteOpenRequest] = useState(0);
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  // Club-wide view: the operator (is_global_admin) OR a club admin on their
  // own host. Both see every team in the club + the full account directory
  // (club-admin parity — `GET /api/admin/teams` and `/api/users` are already
  // host-club-scoped for a club admin). A plain team admin stays scoped to
  // the teams they manage.
  const canSeeClubWide = isGlobalAdmin || !!me?.is_club_admin;
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: canSeeClubWide,
    staleTime: 60_000,
  });
  const manageableTeams = useMemo(
    () =>
      canSeeClubWide
        ? normalizeInviteTeams(
            (adminTeamsQuery.data ?? []).map((team) => ({
              id: team.id,
              name: team.name,
              seasonName: seasonNameOf(team.season_id, me?.seasons),
              archived: team.status === "archived",
            })),
          )
        : normalizeInviteTeams(
            (me?.teams ?? [])
              .filter((team) => team.capabilities.includes("membership:manage"))
              .map((team) => ({
                id: team.id,
                name: team.name,
                seasonName: team.season?.name ?? null,
                archived: team.status === "archived",
              })),
          ),
    [adminTeamsQuery.data, canSeeClubWide, me?.seasons, me?.teams],
  );
  const canManage = !!(canSeeClubWide || manageableTeams.length > 0);

  const pageHeader = (
    <AdminPageHeader
      eyebrow="Club setup"
      title="People"
      description="Memberships, invitations and consent, in one place."
      actions={
        <Button
          type="button"
          size="sm"
          onClick={() => setInviteOpenRequest((request) => request + 1)}
          data-testid="admin-people-header-invite"
        >
          Invite person
        </Button>
      }
    />
  );

  if (!canManage) {
    return (
      <div data-testid="admin-people" className="p-4">
        <p
          data-testid="admin-people-denied"
          className="text-destructive text-sm"
        >
          You don't have permission to manage access on this team.
        </p>
      </div>
    );
  }

  const pendingInvites = (
    <section
      id="admin-people-pending-invites"
      data-testid="admin-people-pending-invites"
      className="people-panel flex flex-col gap-3"
    >
      <header className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          <p className="people-panel-eyebrow">Joining the club</p>
          <h3 className="people-panel-title">Invitations</h3>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setInviteOpenRequest((request) => request + 1)}
          data-testid="admin-people-invite-new"
        >
          Invite person
        </Button>
      </header>

      <InvitesPanel
        teams={manageableTeams}
        teamsLoading={canSeeClubWide && adminTeamsQuery.isLoading}
        openRequest={inviteOpenRequest}
      />
    </section>
  );

  return (
    <div data-testid="admin-people" className="flex flex-col gap-4">
      {pageHeader}

      <AccessRequestsPanel />
      {/* The endpoint resolves a CLUB role (`roster:manage` via
          require_club_role), so gate on the same audience the server
          enforces — a plain team admin used to see this panel render
          and then sit permanently on "Failed to load". */}
      {canSeeClubWide ? <TeamConsentsPanel teams={manageableTeams} /> : null}
      {pendingInvites}
      {canSeeClubWide ? <AdminUsersPage embedded /> : null}
    </div>
  );
}

function AccessRequestsPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [target, setTarget] = useState<AccessRequestRow | null>(null);
  const query = useQuery({
    queryKey: unscopedKey("admin-access-requests"),
    queryFn: getAdminAccessRequests,
    staleTime: 30_000,
  });
  const rows = query.data ?? [];
  const accessCollection = useCollection(rows, {
    scope: "access-requests",
    text: (r) =>
      `${r.requester.display_name} ${r.requester.username} ${r.requester.email ?? ""}`,
    matches: (r, status) => r.status === status,
  });
  const pendingCount = rows.filter((row) => row.status === "pending").length;

  const approve = useMutation({
    mutationFn: ({ row, note }: { row: AccessRequestRow; note: string }) =>
      approveAccessRequest(row.id, note),
    onSuccess: async () => {
      toast.success("Access approved");
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-access-requests"),
      });
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-users"),
      });
      setTarget(null);
    },
    onError: (err) =>
      toast.error("Couldn't approve request", { description: formatErr(err) }),
  });
  const reject = useMutation({
    mutationFn: ({ row, note }: { row: AccessRequestRow; note: string }) =>
      rejectAccessRequest(row.id, note),
    onSuccess: async () => {
      toast.success("Access rejected");
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-access-requests"),
      });
      setTarget(null);
    },
    onError: (err) =>
      toast.error("Couldn't reject request", { description: formatErr(err) }),
  });

  return (
    <section
      id="admin-people-access-requests"
      data-testid="admin-people-access-requests"
      className="people-panel flex flex-col gap-3"
    >
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="people-panel-eyebrow">Waiting for a decision</p>
          <h3 className="people-panel-title">Access requests</h3>
        </div>
        <span className="rounded-full border border-rule bg-elevated px-2 py-1 font-mono text-xs text-ink-muted">
          {pendingCount} pending
        </span>
      </header>
      <CollectionToolbar
        collection={accessCollection}
        label="access requests"
        filters={[
          { value: "", label: "All statuses" },
          { value: "pending", label: "Pending" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
        ]}
      />
      {query.isError ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
          Failed to load access requests.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-rule bg-surface p-3 text-sm text-ink-muted">
          No access requests yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded border border-rule bg-surface">
          <div className="max-h-96 overflow-auto">
            {accessCollection.items.map((row) => (
              <div
                key={row.id}
                data-testid="admin-access-request-row"
                className="grid gap-3 border-b border-rule px-3 py-3 last:border-b-0 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">
                    {row.requester.display_name || row.requester.username}
                  </div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {row.requester.email || "No email"} · {teamSeasonLabel(row.team.name, row.team.season_name)} ·{" "}
                    {row.requested_role.replace("_", " ")}
                  </div>
                  {row.requester_note ? (
                    <p className="mt-1 line-clamp-2 text-xs text-ink-muted">
                      {row.requester_note}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center justify-end gap-2">
                  {row.status !== "pending" ? (
                    <span
                      data-status={row.status}
                      className="inline-flex rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted data-[status=approved]:border-good/40 data-[status=approved]:text-good data-[status=rejected]:border-flag/40 data-[status=rejected]:text-flag"
                    >
                      {row.status === "rejected" ? "Declined" : "Approved"}
                    </span>
                  ) : null}
                  {row.status === "pending" &&
                  (row.has_consent === false ||
                    row.consent_version_current === false) ? (
                    // Approval refuses on two grounds: no usable affirmation
                    // on the row, or one captured under superseded wording.
                    // Flag BOTH before the click — checking only the first
                    // let a stale request look clean and 409 on Approve.
                    //
                    // Wording is deliberately "check", not "will be refused":
                    // approval also accepts a separately recorded
                    // consent_records row (the operator's offline fallback),
                    // so this is a prompt to verify, not a guaranteed block.
                    <span
                      data-testid="access-request-consent-chip"
                      data-reason={
                        row.has_consent === false ? "missing" : "stale"
                      }
                      title={
                        row.has_consent === false
                          ? "No usable affirmation on this request. Approval is refused unless consent was recorded separately from the roster."
                          : "This affirmation was captured under an older version of the documents. Approval will ask them to re-submit."
                      }
                      className="inline-flex rounded-full border border-flag/40 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-flag"
                    >
                      {row.has_consent === false
                        ? "Check consent"
                        : "Consent out of date"}
                    </span>
                  ) : null}
                  {row.status === "pending" ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setTarget(row)}
                      data-testid="admin-access-request-review"
                    >
                      Review request
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <CollectionPagination collection={accessCollection} />
      {target ? (
        <AccessRequestReviewDialog
          row={target}
          pending={approve.isPending || reject.isPending}
          onApprove={(note) => approve.mutate({ row: target, note })}
          onReject={(note) => reject.mutate({ row: target, note })}
          onClose={() => setTarget(null)}
        />
      ) : null}
    </section>
  );
}

function AccessRequestReviewDialog({
  row,
  pending,
  onApprove,
  onReject,
  onClose,
}: {
  row: AccessRequestRow;
  pending: boolean;
  onApprove: (note: string) => void;
  onReject: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState(row.resolution_note ?? "");
  const requester = row.requester.display_name || row.requester.username;
  const initials = requester
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return (
    <ConfirmDialog
      title="Review access request"
      message={
        <div className="admin-request-review">
          <div className="admin-request-person">
            <span className="admin-request-avatar" aria-hidden>{initials}</span>
            <div>
              <strong>{requester}</strong>
              <small>{row.requester.email || `@${row.requester.username}`}</small>
            </div>
          </div>
          <div className="admin-request-access">
            <span>Requested access</span>
            <p>
              <strong>{teamSeasonLabel(row.team.name, row.team.season_name)}</strong> · {row.requested_role.replaceAll("_", " ")}
            </p>
            <p>{row.requester_note || "No note included."}</p>
          </div>
          <p className="admin-request-helper">
            {row.team.archived
              ? "This team's season has ended, so the request can only be declined. The person can request the current season's team."
              : "Approval grants the requested team membership. Player linking is a separate operational step."}
          </p>
          <label className="admin-request-resolution">
            <span>Decision note</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={1000}
              placeholder="Optional note for the access record"
              data-testid="admin-access-request-resolution-note"
            />
          </label>
        </div>
      }
      cancelLabel={null}
      confirmLabel={pending ? "Approving…" : "Approve request"}
      // An archived team takes no new members (#908): approving is refused.
      confirmDisabled={pending || !!row.team.archived}
      altAction={{
        label: pending ? "Working…" : "Decline request",
        danger: true,
        onClick: () => onReject(note),
        "data-testid": "admin-access-request-reject",
      }}
      onConfirm={() => onApprove(note)}
      onClose={onClose}
      data-testid="admin-access-request-review-dialog"
    />
  );
}

// ---------------------------------------------------------------------------
// E5 / E6 / E7 — Invites panel
// ---------------------------------------------------------------------------

function InvitesPanel({
  teams,
  teamsLoading,
  openRequest,
}: {
  teams: InviteTeamOption[];
  teamsLoading: boolean;
  openRequest: number;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const teamIdsKey = useMemo(
    () =>
      teams
        .map((team) => team.id)
        .sort()
        .join("|"),
    [teams],
  );
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of teams)
      map.set(team.id, teamSeasonLabel(team.name, team.seasonName, "Legacy"));
    return map;
  }, [teams]);
  const query = useQuery({
    queryKey: unscopedKey("admin-people-invites", { teamIds: teamIdsKey }),
    queryFn: async () => {
      const perTeam = await Promise.all(
        teams.map((team) => getTeamInvites(team.id)),
      );
      return perTeam.flat().sort((a, b) => {
        const aCreated = Number(a.created_at ?? 0);
        const bCreated = Number(b.created_at ?? 0);
        if (aCreated !== bCreated) return bCreated - aCreated;
        return b.id.localeCompare(a.id);
      });
    },
    enabled: teams.length > 0,
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<TeamInviteRow | null>(null);
  // E7 — dev-token surface: stash rows that came back from a
  // create/resend with `invite_token` so the operator can copy them.
  // We hold the full row optimistically too — if a fresh create
  // hasn't been picked up by the list refetch yet (the row is
  // brand-new), the optimistic copy keeps the dev-link visible
  // until the GET catches up. Keyed by invite id so a refresh
  // overwrites the prior URL.
  const [devTokens, setDevTokens] = useState<Record<string, string>>({});
  const [optimisticRows, setOptimisticRows] = useState<TeamInviteRow[]>([]);

  useEffect(() => {
    if (openRequest > 0) setComposerOpen(true);
  }, [openRequest]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-people-invites"),
    });

  const resend = useMutation({
    mutationFn: (invite: TeamInviteRow) =>
      resendTeamInvite(invite.id, invite.team_id),
    onSuccess: (row) => {
      if (row.invite_token) {
        setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
      }
      toast.success("Invite resent");
      void invalidate();
    },
    onError: (err) => {
      toast.error("Couldn't resend invite", { description: formatErr(err) });
    },
  });

  // Merge optimistic rows + server list, deduped by id (server
  // wins once the GET resolves). Re-review P1 defense-in-depth:
  // filter optimistic rows by the currently manageable team ids so
  // a stale entry from a different tenant can never leak into the
  // rendered list.
  const visibleRows: TeamInviteRow[] = useMemo(() => {
    // An archived team's invite lapsed with its season (#908): it can't be
    // accepted, resent or revoked, so it is no longer actionable here.
    const archivedTeamIds = new Set(
      teams.filter((team) => team.archived).map((team) => team.id),
    );
    const fromServer = (query.data ?? []).filter((r) =>
      isInviteVisibleInPendingList(r, archivedTeamIds),
    );
    const knownIds = new Set(fromServer.map((r) => r.id));
    const allowedTeamIds = new Set(teams.map((team) => team.id));
    const stillPending = optimisticRows.filter(
      (r) =>
        allowedTeamIds.has(r.team_id) &&
        !knownIds.has(r.id) &&
        isInviteVisibleInPendingList(r, archivedTeamIds),
    );
    return [...stillPending, ...fromServer];
  }, [query.data, optimisticRows, teams]);

  const invitations = useCollection(visibleRows, {
    scope: `invitations:${teams.map((t) => t.id).join(":")}`,
    text: (r) =>
      `${r.email ?? ""} ${r.role} ${teamNameById.get(r.team_id) ?? ""}`,
    matches: (r, status) => inviteDisplayStatus(r) === status,
  });

  return (
    <section
      data-testid="admin-people-invites"
      className="flex flex-col gap-2"
    >
      {query.error ? (
        <p
          data-testid="admin-people-invites-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load invites.
        </p>
      ) : null}

      {visibleRows.length === 0 && !query.isLoading ? (
        <p
          data-testid="admin-people-invites-empty"
          className="text-xs font-mono text-ink-muted"
        >
          {teamsLoading
            ? "Loading teams…"
            : teams.length === 0
              ? "No manageable teams available."
              : "No invites sent yet."}
        </p>
      ) : null}

      <CollectionToolbar collection={invitations} label="invitations" />
      {visibleRows.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {invitations.items.map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              teamName={teamNameById.get(invite.team_id) ?? invite.team_id}
              devTokenUrl={devTokens[invite.id]}
              onResend={() => resend.mutate(invite)}
              onRevoke={() => setRevokeTarget(invite)}
              resendPending={
                resend.isPending && resend.variables?.id === invite.id
              }
            />
          ))}
        </ul>
      ) : null}

      <CollectionPagination collection={invitations} />
      {composerOpen ? (
        <InviteComposerModal
          teams={teams.filter((team) => !team.archived)}
          teamsLoading={teamsLoading}
          onClose={() => setComposerOpen(false)}
          onSuccess={(row) => {
            if (row.invite_token) {
              setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
            }
            // Optimistic insert — the dev-link surfaces immediately
            // even before the list refetch resolves. Once the GET
            // returns, the server row replaces this entry (matched
            // by id, deduped in `visibleRows`).
            setOptimisticRows((rows) => [
              row,
              ...rows.filter((r) => r.id !== row.id),
            ]);
            invitations.clear();
            setComposerOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeInviteConfirm
          teamId={revokeTarget.team_id}
          invite={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => {
            setRevokeTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </section>
  );
}

function InviteRow({
  invite,
  teamName,
  devTokenUrl,
  onResend,
  onRevoke,
  resendPending,
}: {
  invite: TeamInviteRow;
  teamName: string;
  devTokenUrl: string | undefined;
  onResend: () => void;
  onRevoke: () => void;
  resendPending: boolean;
}) {
  const status = inviteDisplayStatus(invite, Date.now());
  const canManageInvite = isInviteActionable(invite);
  return (
    <li
      data-testid="admin-people-invite-row"
      data-invite-id={invite.id}
      data-invite-status={status}
      className="rounded border border-rule/60 bg-elevated px-2 py-1.5"
    >
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs">
        <div className="min-w-0">
          <div className="text-ink truncate">{invite.email}</div>
          <div className="flex flex-wrap items-center gap-1 text-[0.6rem] font-mono text-ink-muted">
            <span
              data-testid="admin-people-invite-row-team"
              data-team-id={invite.team_id}
              className="rounded bg-surface px-1 py-0.5 text-ink"
            >
              {teamName}
            </span>
            <span>{expiryLabel(invite.expires_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <InviteRolePill role={invite.role} />
          <InviteStatusPill status={status} />
          <DeliveryPill
            status={invite.last_delivery_status ?? null}
            isPending={canManageInvite}
          />
        </div>
        <div className="inline-flex gap-1">
          {canManageInvite ? (
            <AdminIconButton
              icon={Send}
              label={resendPending ? "Resending invite" : "Resend invite"}
              onClick={onResend}
              disabled={resendPending}
              data-testid="admin-people-invite-resend"
            />
          ) : null}
          {canManageInvite ? (
            <AdminIconButton
              icon={Ban}
              label="Revoke invite"
              onClick={onRevoke}
              data-testid="admin-people-invite-revoke"
              tone="destructive"
            />
          ) : null}
        </div>
      </div>
      {/* E7 — dev-token copy-link, rendered ONCE per row on the row
       * that just got a fresh token (never logged, never in the
       * success banner — gated control only). */}
      {devTokenUrl ? (
        <div
          data-testid="admin-people-invite-dev-link"
          data-invite-id={invite.id}
          className="mt-1.5 flex items-center gap-1 text-[0.6rem] font-mono"
        >
          <span className="text-ink-muted">Dev invite URL:</span>
          <code className="text-ink break-all">{devTokenUrl}</code>
          <AdminIconButton
            icon={Copy}
            label="Copy invite link"
            onClick={() =>
              navigator.clipboard?.writeText(buildInviteUrl(devTokenUrl))
            }
            data-testid="admin-people-invite-dev-copy"
          />
        </div>
      ) : null}
    </li>
  );
}

export function inviteDisplayStatus(
  invite: TeamInviteRow,
  nowMs = Date.now(),
): string {
  if (
    invite.status === "pending" &&
    invite.expires_at != null &&
    invite.expires_at * 1000 < nowMs
  ) {
    return "expired";
  }
  return invite.status || "pending";
}

export function isInviteActionable(invite: TeamInviteRow): boolean {
  return invite.status === "pending";
}

/** A pending invite, unless its team is archived: that invite lapsed with
 * its season (#908) and can't be accepted, resent or revoked. */
export function isInviteVisibleInPendingList(
  invite: TeamInviteRow,
  archivedTeamIds: ReadonlySet<string> = new Set(),
): boolean {
  return invite.status === "pending" && !archivedTeamIds.has(invite.team_id);
}

function InviteStatusPill({ status }: { status: string }) {
  const tone =
    status === "pending"
      ? "border-accent text-accent"
      : status === "accepted"
        ? "border-good/40 text-good"
        : status === "revoked"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-people-invite-status-pill"
      data-status={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {status}
    </span>
  );
}

function InviteRolePill({ role }: { role: string }) {
  const colors: Record<string, string> = {
    team_admin: "border-accent text-accent",
    coach: "border-good/40 text-good",
    assistant_coach: "border-good/40 text-good",
    player: "border-rule text-ink",
    guardian: "border-rule text-ink-muted",
  };
  const tone = colors[role] ?? "border-rule text-ink-muted";
  const label =
    INVITE_ROLES.find((r) => r.value === role)?.label ?? role.replace("_", " ");
  return (
    <span
      data-testid="admin-people-role-pill"
      data-role={role}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

// E5 — Delivery pill enumerates the four legacy `last_delivery_status`
// values: sent / disabled / not_configured / failed.
function DeliveryPill({
  status,
  isPending,
}: {
  status: string | null;
  isPending: boolean;
}) {
  if (!isPending) return null;
  const map: Record<string, { label: string; tone: string }> = {
    sent: { label: "Email sent", tone: "border-good/40 text-good" },
    disabled: { label: "Email off", tone: "border-rule text-ink-muted" },
    not_configured: {
      label: "Email not configured",
      tone: "border-rule text-ink-muted",
    },
    failed: {
      label: "Send failed",
      tone: "border-destructive/40 text-destructive",
    },
  };
  const entry = status ? map[status] : null;
  if (!entry) {
    return (
      <span
        data-testid="admin-people-invite-delivery-pill"
        data-delivery="awaiting"
        className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
      >
        awaiting
      </span>
    );
  }
  return (
    <span
      data-testid="admin-people-invite-delivery-pill"
      data-delivery={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${entry.tone}`}
    >
      {entry.label}
    </span>
  );
}

function expiryLabel(expiresAt: number | null | undefined): string {
  if (!expiresAt) return "no expiry on file";
  const ms = expiresAt * 1000;
  const diff = ms - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 1) return `expires in ${hours}h`;
  return "expires in <1h";
}

function buildInviteUrl(token: string): string {
  // The Tktic SPA's invite-accept landing is `/invite/{token}` (a
  // route registered in router.tsx in M9). For dev-token copies
  // we surface the same path — the M9 landing handles both the
  // signed-in accept and the create-account branches.
  if (typeof window === "undefined") return `/invite/${token}`;
  return `${window.location.origin}/invite/${token}`;
}

function InviteComposerModal({
  teams,
  teamsLoading,
  onClose,
  onSuccess,
}: {
  teams: InviteTeamOption[];
  teamsLoading: boolean;
  onClose: () => void;
  onSuccess: (row: TeamInviteRow) => void;
}) {
  const toast = useToast();
  const teamOptions = useMemo(
    () => [
      ...(teams.length > 1
        ? [{ value: "", label: "Pick a team", disabled: true }]
        : []),
      ...teams.map((team) => ({
        value: team.id,
        label: teamSeasonLabel(team.name, team.seasonName, "Legacy"),
      })),
    ],
    [teams],
  );
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coach");
  const [chosenTeamId, setSelectedTeamId] = useState(
    teams.length === 1 ? (teams[0]?.id ?? "") : "",
  );
  const selectedTeamId =
    teams.length === 1
      ? teams[0].id
      : teams.some((t) => t.id === chosenTeamId)
        ? chosenTeamId
        : "";
  const [playerIds, setPlayerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (teams.length === 1) {
      setSelectedTeamId(teams[0]?.id ?? "");
      return;
    }
    if (selectedTeamId && !teams.some((team) => team.id === selectedTeamId)) {
      setSelectedTeamId("");
    }
  }, [selectedTeamId, teams]);

  // Roster is fetched only when role is player/guardian and the modal
  // has a concrete selected team. This keeps player linking tied to
  // the invite's row-owned team rather than the admin shell scope.
  const rosterEnabled =
    !!selectedTeamId && (role === "player" || role === "guardian");
  const rosterQuery = useQuery({
    queryKey: unscopedKey("admin-people-invite-roster", {
      teamId: selectedTeamId || "none",
    }),
    queryFn: () =>
      apiFetch<{ players: Player[] }>("/api/coach/players", {
        scope: { teamId: selectedTeamId, seasonId: null },
      }),
    enabled: rosterEnabled,
  });
  // #908: each roster row is one ASSIGNMENT of an athlete (`athlete_id`);
  // ended assignments (transferred out / removed) are not linkable.
  const players: Player[] = rosterEnabled
    ? (rosterQuery.data?.players ?? []).filter(
        (p) => p.assignment_status !== "ended",
      )
    : [];
  const selectedTeam = teams.find((team) => team.id === selectedTeamId);
  const teamChip = selectedTeam
    ? teamSeasonLabel(selectedTeam.name, selectedTeam.seasonName, "Legacy")
    : null;

  const invitePlayers = useCollection(players, {
    scope: `invite-players:${selectedTeamId}`,
    text: (p) => `${p.display_name} ${p.jersey_number ?? ""}`,
  });

  const mut = useMutation({
    mutationFn: () =>
      createTeamInvite({
        team_id: selectedTeamId,
        email,
        role,
        player_ids: rosterEnabled ? playerIds : undefined,
      }),
    onSuccess: (row) => {
      toast.success("Invite sent");
      onSuccess(row);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Invite someone to a team"
      confirmDisabled={teamsLoading || !selectedTeamId || mut.isPending}
      message={
        <div className="admin-invite-dialog-form">
          <p className="admin-dialog-helper">
            Choose the role and team. We’ll email an invitation to this
            address.
          </p>
          <label>
            <span>Email address *</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-people-invite-email"
              required
            />
          </label>
          <div className="admin-dialog-two-column">
            <label>
              <span>Team role *</span>
              <TkticSelect
                value={role}
                onChange={(next) => {
                  setRole(next);
                  if (next !== "player" && next !== "guardian") {
                    setPlayerIds([]);
                  }
                }}
                options={INVITE_ROLES}
                ariaLabel="Invite role"
                data-testid="admin-people-invite-role"
              />
            </label>
            <label>
              <span>Team *</span>
              <TkticSelect
                value={selectedTeamId}
                onChange={(next) => {
                  setSelectedTeamId(next);
                  setPlayerIds([]);
                }}
                options={teamOptions}
                disabled={teamsLoading || teams.length <= 1}
                placeholder={teamsLoading ? "Loading teams…" : "Pick a team"}
                ariaLabel="Invite team"
                data-testid="admin-people-invite-team"
              />
            </label>
          </div>

          {rosterEnabled ? (
            <div className="flex flex-col gap-1">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Link to players
              </span>
              <CollectionToolbar
                collection={invitePlayers}
                label="players to link"
              />
              {players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  {rosterQuery.isLoading
                    ? "Loading roster…"
                    : "No players on roster yet."}
                </p>
              ) : (
                <div
                  data-testid="admin-people-invite-player-list"
                  className="flex flex-col gap-0.5 max-h-40 overflow-auto rounded border border-rule bg-elevated p-1"
                >
                  {invitePlayers.items.map((p) => {
                    const selected = playerIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setPlayerIds((ids) =>
                            selected
                              ? ids.filter((x) => x !== p.id)
                              : [...ids, p.id],
                          );
                        }}
                        data-testid={`admin-people-invite-player-${p.id}`}
                        data-active={selected ? "true" : undefined}
                        className="flex items-center gap-1 w-full rounded px-1 py-0.5 text-xs text-left transition-colors hover:bg-field data-[active=true]:text-accent"
                      >
                        <span className="text-ink">
                          {p.display_name || p.id}
                          {p.jersey_number ? (
                            <span className="text-ink-muted">
                              {" "}
                              #{p.jersey_number}
                            </span>
                          ) : null}
                        </span>
                        <span className="ml-auto inline-flex items-center gap-1">
                          {teamChip ? (
                            <span
                              data-testid="admin-people-invite-player-team"
                              className="rounded-full border border-rule bg-elevated px-1.5 py-0.5 font-mono text-[0.6rem] text-ink-muted"
                            >
                              {teamChip}
                            </span>
                          ) : null}
                          {selected ? (
                            <span className="text-accent">✓</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <CollectionPagination collection={invitePlayers} />
            </div>
          ) : !selectedTeamId && (role === "player" || role === "guardian") ? (
            <p
              data-testid="admin-people-invite-player-scope-note"
              className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-mono text-ink-muted"
            >
              Pick a team before linking players.
            </p>
          ) : null}

          {error ? (
            <p
              data-testid="admin-people-invite-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Sending…" : "Send invite"}
      onConfirm={() => {
        setError(null);
        if (!selectedTeamId) {
          setError("Pick a team.");
          return;
        }
        if (!email.trim() || email.length < 3) {
          setError("Email is required.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-composer"
    />
  );
}

function RevokeInviteConfirm({
  teamId,
  invite,
  onClose,
  onSuccess,
}: {
  teamId: string;
  invite: TeamInviteRow;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => revokeTeamInvite(invite.id, teamId),
    onSuccess: () => {
      toast.success("Invite revoked");
      onSuccess();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't revoke invite", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Revoke invite?"
      message={
        <>
          <p>
            The invite to <strong>{invite.email}</strong> will be cancelled. Any
            pending link becomes invalid.
          </p>
          {error ? (
            <p
              data-testid="admin-people-invite-revoke-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Revoking…" : "Revoke invite"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-revoke-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

/** Piece 2 of the consent-visibility plan: the staff answer to "who has
 * consented, to what, and when" — per-player evidence rows plus the
 * withdrawal queue, per team. Audience matches the server gate
 * (`roster:manage`: club admin + operator); the payload is roster-tier
 * data and never carries the operator's consent note. */
function formatConsentDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type ClubConsentPlayer = TeamConsentPlayerRow & {
  team_id: string;
  team_name: string;
};

type ClubConsentWithdrawal = ConsentWithdrawalRow & {
  team_id: string;
  team_name: string;
};

type ConsentRecordTarget = {
  player: ClubConsentPlayer | null;
  withdrawal: ClubConsentWithdrawal | null;
};

function TeamConsentsPanel({ teams }: { teams: InviteTeamOption[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [teamFilter, setTeamFilter] = useState("");
  const [recordTarget, setRecordTarget] = useState<ConsentRecordTarget | null>(
    null,
  );
  const [resolutionTarget, setResolutionTarget] =
    useState<ConsentRecordTarget | null>(null);
  const teamIdsKey = teams.map((team) => team.id).sort().join("|");
  const query = useQuery({
    queryKey: unscopedKey("admin-club-consents", { teamIds: teamIdsKey }),
    queryFn: async () => {
      const responses = await Promise.all(
        teams.map(async (team) => ({
          team,
          overview: await getAdminTeamConsents(team.id),
        })),
      );
      return {
        players: responses.flatMap(({ team, overview }) =>
          overview.players.map((player) => ({
            ...player,
            team_id: team.id,
            team_name: team.name,
          })),
        ),
        withdrawals: responses.flatMap(({ team, overview }) =>
          overview.withdrawals.map((withdrawal) => ({
            ...withdrawal,
            team_id: team.id,
            team_name: team.name,
          })),
        ),
      };
    },
    enabled: teams.length > 0,
    staleTime: 30_000,
  });
  const resolve = useMutation({
    mutationFn: ({
      teamId,
      withdrawalId,
      note,
    }: {
      teamId: string;
      withdrawalId: string;
      note: string;
    }) => resolveConsentWithdrawal(teamId, withdrawalId, note),
    onSuccess: async () => {
      toast.success("Withdrawal marked handled");
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-club-consents"),
      });
      setResolutionTarget(null);
      setRecordTarget(null);
    },
    onError: (err) =>
      toast.error("Couldn't resolve the withdrawal", {
        description: formatErr(err),
      }),
  });

  const overview = query.data;
  const pendingWithdrawals =
    overview?.withdrawals.filter((w) => w.status === "pending") ?? [];
  const consentCounts = {
    current:
      overview?.players.filter((player) => player.consent_status === "current")
        .length ?? 0,
    stale:
      overview?.players.filter((player) => player.consent_status === "stale")
        .length ?? 0,
    missing:
      overview?.players.filter((player) => player.consent_status === "missing")
        .length ?? 0,
    withdrawn:
      overview?.players.filter(
        (player) => player.consent_status === "withdrawn",
      ).length ?? 0,
  };
  const consentRows = useCollection(overview?.players ?? [], {
    scope: `consents:${teamIdsKey}`,
    text: (p) =>
      `${p.display_name} ${p.jersey_number ?? ""} ${p.team_name}`,
    matches: (p, f) => p.consent_status === f,
    predicate: (p) => !teamFilter || p.team_id === teamFilter,
  });
  const withdrawals = useCollection(pendingWithdrawals, {
    scope: `withdrawals:${teamIdsKey}`,
    text: (w) =>
      `${w.requested_by} ${w.player_name ?? ""} ${w.message ?? ""} ${w.team_name}`,
  });
  if (teams.length === 0) return null;

  return (
    <section
      id="admin-people-consents"
      data-testid="admin-team-consents"
      className="people-panel flex flex-col gap-3"
    >
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="people-panel-eyebrow">Player media permissions</p>
          <h3 className="people-panel-title">Consent overview</h3>
        </div>
        <Link to="/me" search={{ tab: "consent" }} className="people-row-action">
          Account consent
        </Link>
      </header>
      {query.isError ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
          Failed to load consent records.
        </p>
      ) : !overview ? (
        <p className="rounded border border-rule bg-surface p-3 text-sm text-ink-muted">
          Loading consent records…
        </p>
      ) : (
        <>
          <div className="people-consent-metrics" aria-label="Consent status totals">
            {(
              ["current", "stale", "missing", "withdrawn"] as const
            ).map((status) => (
              <div key={status} data-consent-status={status}>
                <span>{status}</span>
                <strong>{consentCounts[status]}</strong>
              </div>
            ))}
          </div>
          {pendingWithdrawals.length > 0 ? (
            <div className="rounded border border-flag/40 bg-flag/5 p-3">
              <p className="text-xs font-display uppercase tracking-wider text-flag">
                Withdrawal requests
              </p>
              <div className="mt-2 space-y-2">
                <CollectionToolbar
                  collection={withdrawals}
                  label="withdrawal requests"
                />
                {withdrawals.items.map((w) => (
                  <div
                    key={`${w.team_id}:${w.id}`}
                    data-testid="admin-consent-withdrawal-row"
                    className="flex flex-wrap items-center justify-between gap-2 rounded border border-rule bg-surface px-3 py-2"
                  >
                    <div className="min-w-0 text-sm text-ink">
                      <span className="font-medium">{w.requested_by}</span>
                      {w.player_name ? ` · ${w.player_name}` : " · whole team"}
                      {` · ${w.team_name}`}
                      {w.message ? (
                        <p className="mt-0.5 text-xs text-ink-muted">
                          {w.message}
                        </p>
                      ) : w.message_withheld ? (
                        <p className="mt-0.5 text-xs italic text-ink-muted">
                          The family included a message for the club. Open this
                          on the club's own host to read it.
                        </p>
                      ) : null}
                      <p className="mt-0.5 font-mono text-[0.65rem] text-ink-muted">
                        {formatConsentDate(w.created_at)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="admin-consent-withdrawal-view"
                      onClick={() =>
                        setRecordTarget({
                          player:
                            overview.players.find(
                              (player) =>
                                player.team_id === w.team_id &&
                                player.id === w.player_id,
                            ) ?? null,
                          withdrawal: w,
                        })
                      }
                    >
                      View record
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <CollectionPagination collection={withdrawals} />
          <CollectionToolbar
            collection={consentRows}
            label="consent records"
            extraFilters={[
              {
                label: "Team",
                ariaLabel: "Filter consent records by team",
                value: teamFilter,
                clearValue: "",
                options: [
                  { value: "", label: "All" },
                  ...teams.map((team) => ({
                    value: team.id,
                    label: teamSeasonLabel(team.name, team.seasonName, "Legacy"),
                  })),
                ],
                onChange: setTeamFilter,
              },
            ]}
            filters={[
              { value: "", label: "All consent" },
              { value: "current", label: "Current" },
              { value: "missing", label: "Missing" },
              { value: "stale", label: "Stale" },
              { value: "withdrawn", label: "Withdrawn" },
            ]}
          />
          <div className="overflow-hidden rounded border border-rule bg-surface">
            {consentRows.items.map((p) => (
              <div
                key={`${p.team_id}:${p.id}`}
                data-testid="admin-consent-row"
                data-status={p.consent_status}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-rule px-3 py-2 last:border-b-0"
              >
                <div className="min-w-0">
                  <span className="text-sm font-medium text-ink">
                    {p.display_name}
                  </span>
                  {p.jersey_number ? (
                    <span className="ml-1 font-mono text-xs text-ink-muted">
                      #{p.jersey_number}
                    </span>
                  ) : null}
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {p.team_name}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {/* A withdrawal by one guardian no longer voids a
                      co-guardian's consent, so the status can read
                      "current" while a request is open. Surface it here
                      or the parent who asked gets no follow-up. */}
                  {p.withdrawal_pending ? (
                    <span
                      data-testid="admin-consent-withdrawal-pending"
                      title="A guardian has asked to withdraw. Another guardian's consent currently keeps this player covered — follow up with the family."
                      className="inline-flex rounded-full border border-flag/40 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-flag"
                    >
                      Withdrawal open
                    </span>
                  ) : null}
                  <span
                    data-status={p.consent_status}
                    className="inline-flex rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted data-[status=current]:border-good/40 data-[status=current]:text-good data-[status=missing]:border-flag/40 data-[status=missing]:text-flag data-[status=stale]:border-flag/40 data-[status=stale]:text-flag data-[status=withdrawn]:border-flag data-[status=withdrawn]:text-flag"
                  >
                    {p.consent_status === "stale"
                      ? "Stale"
                      : p.consent_status}
                  </span>
                  <button
                    type="button"
                    className="people-row-action"
                    onClick={() =>
                      setRecordTarget({
                        player: p,
                        withdrawal:
                          overview.withdrawals.find(
                            (withdrawal) =>
                              withdrawal.team_id === p.team_id &&
                              withdrawal.player_id === p.id,
                          ) ?? null,
                      })
                    }
                    data-testid="admin-consent-view-record"
                  >
                    View record
                  </button>
                </div>
              </div>
            ))}
          </div>
          <CollectionPagination collection={consentRows} />
          {recordTarget ? (
            <ConsentRecordDialog
              target={recordTarget}
              onRecordResolution={() => {
                setResolutionTarget(recordTarget);
                setRecordTarget(null);
              }}
              onClose={() => setRecordTarget(null)}
            />
          ) : null}
          {resolutionTarget?.withdrawal ? (
            <ConsentResolutionDialog
              target={resolutionTarget}
              pending={resolve.isPending}
              onSave={(note) =>
                resolve.mutate({
                  teamId: resolutionTarget.withdrawal!.team_id,
                  withdrawalId: resolutionTarget.withdrawal!.id,
                  note,
                })
              }
              onClose={() => {
                setResolutionTarget(null);
                setRecordTarget(resolutionTarget);
              }}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

function ConsentRecordDialog({
  target,
  onRecordResolution,
  onClose,
}: {
  target: ConsentRecordTarget;
  onRecordResolution: () => void;
  onClose: () => void;
}) {
  const { player, withdrawal } = target;
  const title = player?.display_name ?? withdrawal?.player_name ?? "Consent record";
  const status = player?.consent_status ?? "missing";
  const explanation = {
    current: "An up-to-date consent record is on file for this player.",
    stale: "Consent was given under earlier wording. A fresh affirmation is needed.",
    missing: "No consent record is on file. Media eligibility remains restricted.",
    withdrawn: "Consent has been withdrawn. Resolving the request does not restore it.",
  }[status];
  return (
    <ConfirmDialog
      title="Consent record"
      message={
        <div className="admin-consent-record">
          <header>
            <div>
              <span>{player?.team_name ?? withdrawal?.team_name}</span>
              <strong>{title}</strong>
            </div>
            {player ? (
              <span data-status={player.consent_status}>
                {player.consent_status === "stale"
                  ? "Stale"
                  : player.consent_status}
              </span>
            ) : null}
          </header>
          <section>
            <p>{explanation}</p>
            {player?.consent ? (
              <div className="admin-consent-evidence">
                {player.consent.consented_by ?? "Recorded by the club"} ·{" "}
                {formatConsentDate(player.consent.created_at)} · version{" "}
                {player.consent.version} ·{" "}
                {player.consent.source === "email"
                  ? "club-recorded email"
                  : "in app"}
              </div>
            ) : (
              <div className="admin-consent-evidence">
                No current media consent is on file.
              </div>
            )}
          </section>
          {withdrawal ? (
            <section className="admin-consent-withdrawal-detail">
              <h3>Withdrawal request</h3>
              {withdrawal.message ? <blockquote>{withdrawal.message}</blockquote> : null}
              {withdrawal.message_withheld ? (
                <p>The family’s message is available only on its club host.</p>
              ) : null}
              {withdrawal.status === "resolved" ? (
                <div className="admin-consent-resolution-saved">
                  <strong>Resolution recorded</strong>
                  <p>{withdrawal.resolution_note || "No resolution note saved."}</p>
                </div>
              ) : (
                <span className="admin-consent-waiting">Waiting for club action</span>
              )}
            </section>
          ) : null}
          <p className="admin-consent-privacy-note">
            Operational consent evidence only. No coaching content appears here.
          </p>
        </div>
      }
      cancelLabel={withdrawal?.status === "pending" ? "Close" : null}
      confirmLabel={withdrawal?.status === "pending" ? "Record resolution" : "Close"}
      onConfirm={withdrawal?.status === "pending" ? onRecordResolution : onClose}
      onClose={onClose}
      wide={!!withdrawal}
      data-testid="admin-consent-record-dialog"
    />
  );
}

function ConsentResolutionDialog({
  target,
  pending,
  onSave,
  onClose,
}: {
  target: ConsentRecordTarget;
  pending: boolean;
  onSave: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  return (
    <ConfirmDialog
      title="Record withdrawal resolution"
      message={
        <div
          className="admin-resolution-form"
          data-player-name={
            target.player?.display_name ?? target.withdrawal?.player_name
          }
        >
          <p>
            Confirm the requested removal work has been completed. Consent
            remains withdrawn.
          </p>
          <label>
            <span>Resolution note *</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={1000}
              required
              placeholder="What was removed or restricted?"
              data-testid="admin-consent-resolution-note"
            />
          </label>
        </div>
      }
      confirmLabel={pending ? "Saving…" : "Record resolution"}
      confirmDisabled={pending || !note.trim()}
      onConfirm={() => onSave(note.trim())}
      onClose={onClose}
      data-testid="admin-consent-resolution-dialog"
    />
  );
}
